import { AppRole } from './roles';

// Backend-verified RBAC matrix — CONTRACTS.md §8. Derived from the actual @PreAuthorize annotations.
// A = administrator, S = system_admin, C = content_manager, M = marketer.
// Fallback seed when effective-permissions has not loaded yet; System & Permissions can override in DB.
const ADMINS: AppRole[] = [AppRole.Administrator, AppRole.SystemAdmin];
const CONTENT: AppRole[] = [AppRole.Administrator, AppRole.SystemAdmin, AppRole.ContentManager];
const CONTENT_MARKETER: AppRole[] = [...CONTENT, AppRole.Marketer];

// Route key -> roles allowed to OPEN it (client guard; backend @PreAuthorize is the real gate).
// operator has NO backend access; marketer is granted dashboard + engagement only.
// finance role is granted only on FinanceController. A client guard cannot grant access the API will 403.
export const ROUTE_ROLES = {
  dashboard: [...CONTENT_MARKETER], // DashboardController + StatisticsController: A,S,C,Marketer
  users: [...CONTENT], // read; change-status is admins-only (button-level FE gate)
  admins: [...ADMINS], // AdminAccountController + UserManager admin/*
  'system-permissions': [AppRole.Administrator], // RolePageAccessController: Administrator only
  content: [...CONTENT], // PostManagerController
  moderation: [...CONTENT], // ModerationController
  media: [...CONTENT], // MediaLibraryController CURATION_ROLES (sidebar entry + /media index)
  'media-stickers': [...CONTENT], // sticker packs: upload + delist
  'media-transitions': [...CONTENT], // .tfx packages carrying kernel-transition/1
  'effect-packs': [...CONTENT], // EffectPackCurationController CURATION_ROLES
  effects: [...CONTENT], // Effects hub shell (tabs re-gate individually below)
  'tfx-create': [...CONTENT], // TfxRenderJobController PROMPT jobs (AI Create tab)
  'tfx-drafts': [...CONTENT], // TfxDraftController — same CONTENT trio as effect-packs
  'tfx-review': [...CONTENT], // tfx review queue (drafts?mine=false&status=SUBMITTED)
  'tfx-published': [...CONTENT], // TfxPublishedController (delist kill switch / relist)
  campaigns: [...ADMINS], // STUB, no backend — placeholder default; confirm w/ product
  finance: [AppRole.Administrator, AppRole.Finance], // FinanceController: Administrator + Finance only
  engagement: [...CONTENT_MARKETER], // EngagementController: A,S,C,Marketer
} satisfies Record<string, AppRole[]>;

export type RouteKey = keyof typeof ROUTE_ROLES;

/** Union-of-permissions: true if the user holds ANY allowed role (empty allowed = open).
 *  Administrator (and legacy System Admin → Administrator) always returns true. */
export function hasAnyRole(userRoles: readonly AppRole[], allowed: readonly AppRole[]): boolean {
  if (userRoles.includes(AppRole.Administrator) || userRoles.includes(AppRole.SystemAdmin)) {
    return true;
  }
  if (allowed.length === 0) return true;
  return userRoles.some((r) => allowed.includes(r));
}

export function canAccess(route: RouteKey, roles: readonly AppRole[]): boolean {
  if (roles.includes(AppRole.Administrator)) return true;
  // System & Permissions is Administrator-only (not System Admin, not matrix-delegable).
  if (route === 'system-permissions') return false;
  if (roles.includes(AppRole.SystemAdmin)) return true;
  return hasAnyRole(roles, ROUTE_ROLES[route]);
}
